<template>
  <div :class="$style.container">
    <HgSelect
      v-model="selectedLocation"
      :disabled="isResolvingLocation"
      :options="locationOptions"
      name="locations-dropdown"
      @update:modelValue="handleLocationChange"
    />
    <HgSegmentedButton
      v-model="selectedPropertyType"
      name="property-type-switcher"
      :disabled="isResolvingLocation"
      :options="propertyTypeOptions"
      :class="$style.switchButton"
      @update:modelValue="handlePropertyTypeChange"
    />
  </div>
</template>
<script setup lang="ts">
import { getLocationInfos } from '@/helpers/GeoLocation/GeoLocationHelper';
import { useLocalLandingStore } from '@/stores/localLanding/localLanding';
import { trackingMapInteractionEvent } from '@/tracking/tracking';
import { LOCAL_LANDING_PAGE_ACTION } from '@/tracking/types';
import { PropertyType } from '@/types';
import {
  HgSegmentedButton,
  HgSelect,
  HgSupportedLanguage,
  Icon,
  type HgSegmentedOptions,
} from '@smg-real-estate/domus';
import { GeoLocation } from '@smg-real-estate/geo-libs';
import capitalize from 'lodash.capitalize';
import { storeToRefs } from 'pinia';
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

// Composables
const localLandingStore = useLocalLandingStore();
const { t, locale } = useI18n();
const { propertyType, geoLocations, selectedGeoLocationId, isResolvingLocation, routeGeoLocation } =
  storeToRefs(localLandingStore);

// Reactive data
const selectedLocation = ref('');
const selectedPropertyType = ref(PropertyType.House);

// Computed properties
const propertyTypeOptions = computed((): HgSegmentedOptions[] => {
  return [
    {
      value: PropertyType.House,
      text: capitalize(t('localLanding.propertyType.house')),
      icon: Icon.House,
    },
    {
      value: PropertyType.Apartment,
      text: capitalize(t('localLanding.propertyType.apartment')),
      icon: Icon.Apartment,
    },
  ];
});

const locationOptions = computed(() => {
  return geoLocations.value.map((geoLocation: GeoLocation) => {
    const { typeText, name } = getLocationInfos(geoLocation, locale.value as HgSupportedLanguage, [
      'canton',
      'municipality',
    ]);
    const text = typeText ? `${capitalize(typeText)}: ${name}` : name;
    return {
      value: geoLocation.id,
      text,
    };
  });
});

// Methods
const handleLocationChange = (locationId: string) => {
  trackingMapInteractionEvent(LOCAL_LANDING_PAGE_ACTION.MARKET_MAP_SEARCH, 'housing_prices_map', {
    h_search_term_geo_080: routeGeoLocation.value ? routeGeoLocation.value.id : '',
    h_search_filter_category_075: propertyType.value,
    h_search_filter_category_sub_084: '',
  });
  localLandingStore.setSelectedGeoLocationId(locationId);
  localLandingStore.fetchPriceData();
};

const handlePropertyTypeChange = (propertyType: string | number) => {
  trackingMapInteractionEvent(`toggle_to_${propertyType}`, 'housing_prices_map');
  localLandingStore.setPropertyType(propertyType as PropertyType);
};

// Watchers
watch(
  selectedGeoLocationId,
  (value: string) => {
    selectedLocation.value = value;
  },
  { immediate: true },
);

// Lifecycle
onMounted(() => {
  selectedPropertyType.value = propertyType.value;
});
</script>
<style lang="scss" module>
@use '@/assets/variables' as *;

$shadow-grow-range: 3px;

.container {
  display: flex;
  flex-direction: column;
  gap: $space-150;
  align-items: center;

  @include grid-media-query(sm) {
    flex-direction: row;
  }

  > div {
    flex: 1;
    width: 100%;
  }

  [class*='HgFormFieldWrapper_noLabel_'] {
    padding-top: 0;
  }
}

.switchButton {
  [class*='HgSegmentedButton_widget'] {
    padding-right: calc($space-25 - $shadow-grow-range);
    padding-left: $space-25;
    background-color: $color-grey-30;

    &[class*='HgSegmentedButton_primary'] [class*='HgSegmentedButton_active'] {
      &:not([class*='HgSegmentedButton_disabled']) {
        color: $color-grey-100;
        background-color: $color-white;
        border-radius: $border-radius;
        box-shadow: $elevation-minimal;
      }
    }

    [class*='HgSegmentedButton_option'] {
      position: relative;
      padding-block: $space-50;
      margin: $space-25 $shadow-grow-range $space-25 0;
      background-color: inherit;
      border: unset;

      @include body-2-bold($color-grey-90);

      & {
        line-height: 1.25;
      }

      &[class*='HgSegmentedButton_hasIcon'] {
        span[class*='HgSegmentedButton_text'] {
          display: unset;
        }
      }
    }
  }
}
</style>
